import { ActionTree } from 'vuex';
import { RootState } from '@/store/state';
import nexusClient from '@/apollo/nexusClient';
import { getAccessToken } from '@/services/secureStorage';
import {
  MY_FAMILIES_QUERY,
  FAMILY_QUERY,
  FAMILY_SUBGRAPH_QUERY,
  FAMILY_VIEW_BY_SHARE_TOKEN_QUERY,
  FAMILY_CREATE_MUTATION,
  FAMILY_UPDATE_MUTATION,
  FAMILY_DELETE_MUTATION,
  FAMILY_DUPLICATE_MUTATION,
  FAMILY_ADD_MEMBER_MUTATION,
  FAMILY_UPDATE_MEMBER_MUTATION,
  FAMILY_DELETE_MEMBER_MUTATION,
  FAMILY_SET_MEMBER_AVATAR_MUTATION,
  FAMILY_CLEAR_MEMBER_AVATAR_MUTATION,
  FAMILY_CLAIM_MEMBER_MUTATION,
  FAMILY_CREATE_RELATIONSHIP_MUTATION,
  FAMILY_DELETE_RELATIONSHIP_MUTATION,
  FAMILY_CREATE_FACE_CROP_MUTATION,
  FAMILY_ASSOCIATE_FACE_TO_MEMBER_MUTATION,
  FAMILY_DELETE_PHOTO_MUTATION,
  FAMILY_CREATE_VIEW_MUTATION,
  FAMILY_UPDATE_VIEW_MUTATION
} from '@/services/graphql/nexus/family';
import { FamilyState } from './state';

// Alias to keep action bodies readable
const apolloNexus = nexusClient;

/**
 * NOTE on the Apollo client import path:
 * `@/services/apolloNexus` is assumed as the existing Nexus client export.
 * If the project uses a different name (e.g. `nexusClient`), update this line.
 * Other Nexus-talking modules in the codebase already import this — follow that pattern.
 */

export const actions: ActionTree<FamilyState, RootState> = {
  // ─── Families list ───

  async fetchMyFamilies({ commit }) {
    commit('SET_FAMILIES_LOADING', true);
    commit('SET_FAMILIES_ERROR', null);
    try {
      const { data } = await apolloNexus.query({
        query: MY_FAMILIES_QUERY,
        fetchPolicy: 'network-only'
      });
      commit('SET_FAMILIES', data?.myFamilies ?? []);
    } catch (err: any) {
      commit('SET_FAMILIES_ERROR', err?.message ?? 'Failed to load families');
    } finally {
      commit('SET_FAMILIES_LOADING', false);
    }
  },

  async createFamily({ commit }, input: { name: string; description?: string }) {
    const { data } = await apolloNexus.mutate({
      mutation: FAMILY_CREATE_MUTATION,
      variables: { input }
    });
    const family = data?.familyCreate;
    if (family) commit('ADD_FAMILY', family);
    return family;
  },

  async updateFamily({ commit }, params: { id: string; name?: string; description?: string }) {
    const { data } = await apolloNexus.mutate({
      mutation: FAMILY_UPDATE_MUTATION,
      variables: params
    });
    const family = data?.familyUpdate;
    if (family) commit('UPDATE_FAMILY', family);
    return family;
  },

  async deleteFamily({ commit }, id: string) {
    const { data } = await apolloNexus.mutate({
      mutation: FAMILY_DELETE_MUTATION,
      variables: { id }
    });
    if (data?.familyDelete) commit('REMOVE_FAMILY', id);
    return data?.familyDelete;
  },

  async duplicateFamily({ commit }, familyId: string) {
    const { data } = await apolloNexus.mutate({
      mutation: FAMILY_DUPLICATE_MUTATION,
      variables: { familyId }
    });
    const newFamily = data?.familyDuplicate;
    if (newFamily) commit('ADD_FAMILY', newFamily);
    return newFamily;
  },

  // ─── Active family subgraph ───

  async loadActiveFamily({ commit }, familyId: string) {
    commit('SET_ACTIVE_FAMILY_ID', familyId);
    commit('SET_ACTIVE_SUBGRAPH_LOADING', true);
    commit('SET_ACTIVE_SUBGRAPH_ERROR', null);
    try {
      const { data } = await apolloNexus.query({
        query: FAMILY_SUBGRAPH_QUERY,
        variables: { familyId },
        fetchPolicy: 'network-only'
      });
      // Apollo freezes response objects — deep-clone so subsequent Vuex mutations
      // (ADD_PHOTO, ADD_MEMBER, etc.) can mutate nested arrays without hitting
      // "Cannot assign to read only property" errors.
      const sg = data?.familySubgraph ? JSON.parse(JSON.stringify(data.familySubgraph)) : null;
      commit('SET_ACTIVE_SUBGRAPH', sg);
    } catch (err: any) {
      commit('SET_ACTIVE_SUBGRAPH_ERROR', err?.message ?? 'Failed to load family');
    } finally {
      commit('SET_ACTIVE_SUBGRAPH_LOADING', false);
    }
  },

  clearActiveFamily({ commit }) {
    commit('SET_ACTIVE_FAMILY_ID', null);
    commit('SET_ACTIVE_SUBGRAPH', null);
    commit('SET_SELECTED_MEMBER_ID', null);
  },

  // ─── Members ───

  async addMember({ commit }, input: any) {
    const { data } = await apolloNexus.mutate({
      mutation: FAMILY_ADD_MEMBER_MUTATION,
      variables: { input }
    });
    const member = data?.familyAddMember;
    if (member) commit('ADD_MEMBER', member);
    return member;
  },

  async updateMember({ commit }, params: { id: string; input: any }) {
    const { data } = await apolloNexus.mutate({
      mutation: FAMILY_UPDATE_MEMBER_MUTATION,
      variables: params
    });
    const member = data?.familyUpdateMember;
    if (member) commit('UPDATE_MEMBER', member);
    return member;
  },

  async deleteMember({ commit }, id: string) {
    const { data } = await apolloNexus.mutate({
      mutation: FAMILY_DELETE_MEMBER_MUTATION,
      variables: { id }
    });
    if (data?.familyDeleteMember) commit('REMOVE_MEMBER', id);
    return data?.familyDeleteMember;
  },

  async setMemberAvatar({ commit }, params: { memberId: string; faceCropId: string }) {
    const { data } = await apolloNexus.mutate({
      mutation: FAMILY_SET_MEMBER_AVATAR_MUTATION,
      variables: params
    });
    const member = data?.familySetMemberAvatar;
    if (member) commit('UPDATE_MEMBER', member);
    return member;
  },

  async clearMemberAvatar({ commit }, memberId: string) {
    const { data } = await apolloNexus.mutate({
      mutation: FAMILY_CLEAR_MEMBER_AVATAR_MUTATION,
      variables: { memberId }
    });
    const member = data?.familyClearMemberAvatar;
    if (member) commit('UPDATE_MEMBER', member);
    return member;
  },

  async claimMember({ commit }, memberId: string) {
    const { data } = await apolloNexus.mutate({
      mutation: FAMILY_CLAIM_MEMBER_MUTATION,
      variables: { memberId }
    });
    const member = data?.familyClaimMember;
    if (member) commit('UPDATE_MEMBER', member);
    return member;
  },

  // ─── Relationships ───

  async createRelationship({ commit }, input: any) {
    const { data } = await apolloNexus.mutate({
      mutation: FAMILY_CREATE_RELATIONSHIP_MUTATION,
      variables: { input }
    });
    const rel = data?.familyCreateRelationship;
    if (rel) commit('ADD_RELATIONSHIP', rel);
    return rel;
  },

  async deleteRelationship({ commit }, id: string) {
    const { data } = await apolloNexus.mutate({
      mutation: FAMILY_DELETE_RELATIONSHIP_MUTATION,
      variables: { id }
    });
    if (data?.familyDeleteRelationship) commit('REMOVE_RELATIONSHIP', id);
    return data?.familyDeleteRelationship;
  },

  // ─── Photos & Face Crops ───

  /**
   * Upload a photo via the REST endpoint.
   * Returns the created FamilyPhoto JSON.
   */
  async uploadPhoto(
    { commit },
    params: {
      familyId: string;
      file: File;
      captionEn?: string;
      captionZh?: string;
      takenAtGuess?: string;
    }
  ) {
    const form = new FormData();
    form.append('file', params.file);
    if (params.captionEn) form.append('captionEn', params.captionEn);
    if (params.captionZh) form.append('captionZh', params.captionZh);
    if (params.takenAtGuess) form.append('takenAtGuess', params.takenAtGuess);

    // The REST endpoint reads the raw body; but multipart is more Portable.
    // If the backend controller needs multipart middleware wired, update it there.
    const authToken = getAccessToken() ?? '';

    // Derive REST base from the GraphQL endpoint (strip /graphql suffix).
    // Matches whatever host Apollo is pointed at — localhost:3007 in dev, production otherwise.
    const graphqlUrl =
      (import.meta as any).env?.VITE_NEXUS_GRAPHQL_HTTP || 'https://services.pilotcity.com/graphql';
    const base = graphqlUrl.replace(/\/graphql\/?$/, '');
    const response = await fetch(`${base}/api/family/photos/${params.familyId}`, {
      method: 'POST',
      headers: {
        Authorization: authToken ? `Bearer ${authToken}` : ''
      },
      body: params.file // raw-body upload; matches the controller implementation
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Photo upload failed: ${response.status} ${text}`);
    }
    const photo = await response.json();
    commit('ADD_PHOTO', photo);
    return photo;
  },

  async createFaceCrop({ commit }, input: any) {
    const { data } = await apolloNexus.mutate({
      mutation: FAMILY_CREATE_FACE_CROP_MUTATION,
      variables: { input }
    });
    const crop = data?.familyCreateFaceCrop;
    if (crop) commit('ADD_FACE_CROP', crop);
    return crop;
  },

  async associateFaceToMember({ commit }, params: { faceCropId: string; memberId: string }) {
    const { data } = await apolloNexus.mutate({
      mutation: FAMILY_ASSOCIATE_FACE_TO_MEMBER_MUTATION,
      variables: params
    });
    const crop = data?.familyAssociateFaceToMember;
    if (crop) commit('UPDATE_FACE_CROP', crop);
    return crop;
  },

  async deletePhoto({ commit }, id: string) {
    const { data } = await apolloNexus.mutate({
      mutation: FAMILY_DELETE_PHOTO_MUTATION,
      variables: { id }
    });
    if (data?.familyDeletePhoto) commit('REMOVE_PHOTO', id);
    return data?.familyDeletePhoto;
  },

  // ─── View / share token ───

  async createView({ commit }, input: any) {
    const { data } = await apolloNexus.mutate({
      mutation: FAMILY_CREATE_VIEW_MUTATION,
      variables: { input }
    });
    return data?.familyCreateView;
  },

  async updateView({ commit }, params: { id: string; input: any }) {
    const { data } = await apolloNexus.mutate({
      mutation: FAMILY_UPDATE_VIEW_MUTATION,
      variables: params
    });
    return data?.familyUpdateView;
  },

  /**
   * Public share-token lookup. No auth. Used by FamilyPublicView.vue.
   */
  async loadPublicSubgraph({ commit }, token: string) {
    commit('SET_PUBLIC_SUBGRAPH_LOADING', true);
    commit('SET_PUBLIC_SUBGRAPH_ERROR', null);
    try {
      const { data } = await apolloNexus.query({
        query: FAMILY_VIEW_BY_SHARE_TOKEN_QUERY,
        variables: { token },
        fetchPolicy: 'network-only'
      });
      const sg = data?.familyViewByShareToken
        ? JSON.parse(JSON.stringify(data.familyViewByShareToken))
        : null;
      commit('SET_PUBLIC_SUBGRAPH', sg);
    } catch (err: any) {
      commit('SET_PUBLIC_SUBGRAPH_ERROR', err?.message ?? 'Tree not found');
    } finally {
      commit('SET_PUBLIC_SUBGRAPH_LOADING', false);
    }
  },

  // ─── UI helpers ───

  setSelectedMember({ commit }, id: string | null) {
    commit('SET_SELECTED_MEMBER_ID', id);
  },
  openWizard({ commit }) {
    commit('SET_WIZARD_OPEN', true);
  },
  closeWizard({ commit }) {
    commit('SET_WIZARD_OPEN', false);
  }
};
